import { useCallback, useMemo } from 'react'
import { formatCurrency } from '../../helpers/formatting'
import { useOpenFin } from '../../helpers/hooks/useOpenFin'
import {
  acceptRfqQuote,
  cancelRfq,
  rejectRfqQuote
} from '../../store/rfq/actions'
import { QuoteRequest, RFQQuote } from '../../store/rfq/types'
import { SecurityStaticData } from '../../store/securities/reducer'
import { useAppDispatch, useAppSelector } from '../../store/types'
import { getExposureDurationValues } from '../../store/webSettings/selectors'
import {
  hideRfqWindow,
  removeRfqWindow,
  showRfqWindow
} from '../../store/windows/actions'
import { getRfqWindow } from '../../store/windows/selectors'
import {
  formatSpread,
  getOppositeDirection,
  orderTypeToOppositeAggressLabel
} from '../DepthOfMarket/helpers'

const inactiveStatuses = [
  'Pass',
  'Rejected',
  'Traded',
  'Cancel',
  'HitLift',
  'QRExpire'
]

export const quoteStatusIsInactive = (quote: RFQQuote) =>
  inactiveStatuses.includes(quote.status)

export const calculateRfqPriceSpreadValues = (
  rfq: QuoteRequest,
  currency: string
) => {
  if (rfq.isSpread) {
    return {
      formattedPrice: formatSpread(rfq.price),
      priceLabel: 'Target Spread:'
    }
  }
  return {
    formattedPrice: formatCurrency(rfq.price, currency),
    priceLabel: 'Target Price:'
  }
}

export const useQuoteRequest = (
  quoteRequest: QuoteRequest,
  security: SecurityStaticData
) => {
  const dispatch = useAppDispatch()
  const exposureDurationValues = useAppSelector(getExposureDurationValues)
  const { isOpenFin, finWindowAction, openPlatformWindow } = useOpenFin()
  const rfqWindowInfo = useAppSelector(getRfqWindow)(
    security.id,
    getOppositeDirection(quoteRequest.type)
  )
  const priceSpreadValues = useMemo(() => {
    return calculateRfqPriceSpreadValues(quoteRequest, security.currency)
  }, [security.currency, quoteRequest.price, quoteRequest.isSpread])

  const subjectLabel = orderTypeToOppositeAggressLabel(quoteRequest.type)

  const acceptQuote = useCallback(
    (id: number) => {
      if (quoteRequest.requestId) {
        dispatch(acceptRfqQuote(security.id, quoteRequest.requestId, id))
      }
    },
    [security.id, quoteRequest.requestId]
  )
  const rejectQuote = useCallback(() => {
    if (quoteRequest.requestId) {
      dispatch(rejectRfqQuote(quoteRequest.requestId, security.id))
    }
  }, [security.id, quoteRequest.requestId])
  const cancelRequest = useCallback(() => {
    dispatch(cancelRfq(security.id))
    if (rfqWindowInfo) {
      if (isOpenFin) {
        void finWindowAction(
          `RFQs/${rfqWindowInfo.orderId}/${security.id}`,
          'close'
        )
      }
      dispatch(removeRfqWindow(rfqWindowInfo))
    }
  }, [security.id, rfqWindowInfo, isOpenFin])

  const handleClose = useCallback(() => {
    if (rfqWindowInfo) {
      if (isOpenFin) {
        void finWindowAction(
          `RFQs/${rfqWindowInfo.orderId}/${security.id}`,
          'close'
        )
      }
      if (quoteRequest.isLive) {
        dispatch(hideRfqWindow(rfqWindowInfo.orderId))
      } else {
        dispatch(removeRfqWindow(rfqWindowInfo))
      }
    }
  }, [rfqWindowInfo, isOpenFin, quoteRequest.isLive, security.id])

  const reopenRfqWindow = useCallback(() => {
    if (rfqWindowInfo) {
      dispatch(showRfqWindow(rfqWindowInfo.orderId))
      if (isOpenFin) {
        void openPlatformWindow(`RFQs/${rfqWindowInfo.orderId}/${security.id}`)
      }
    }
  }, [quoteRequest, security.id, isOpenFin])

  const handleOpen =
    rfqWindowInfo && !rfqWindowInfo.isOpen ? reopenRfqWindow : undefined

  const formatQuotePrice = useCallback(
    (price: number) => {
      if (quoteRequest.isSpread) {
        return formatSpread(price)
      }
      return formatCurrency(price, security.currency)
    },
    [quoteRequest.isSpread, security.currency]
  )

  const formatSize = useCallback((size: number) => {
    return size.toLocaleString()
  }, [])

  const formattedSize = formatSize(quoteRequest.size)

  return {
    ...priceSpreadValues,
    subjectLabel,
    exposureDurationValues,
    acceptQuote,
    rejectQuote,
    cancelRequest,
    handleClose,
    handleOpen,
    formatQuotePrice,
    formatSize,
    formattedSize
  }
}
export const statusMap: Record<string, string> = {
  New: 'Firm',
  HitLift: 'Aggress Pending',
  HitLiftExpire: 'Aggress Attempt Expired',
  QRExpire: 'Expired'
}
export const getDisplayStatus = (quote?: RFQQuote) => {
  if (!quote) {
    return ''
  }
  if (quote.status === 'Update') {
    return quote.isFirm ? 'New' : 'Subject'
  }
  return quote.status
}
